import React, { useCallback, useEffect, useState } from "react";
import {
  listManagedEmployees,
  saveManagedEmployee,
} from "../../../helpers/admin/usersAdmin";
import { AdminFormCard } from "../shared/AdminFormCard";
import { AdminLegacyHeader, AdminListRow } from "../shared/AdminFormCard";
import { CPInput } from "../../CPInput";
import { CPTextarea } from "../../CPTextarea";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskTable } from "../../desktop/DeskTable";
import "../adminDesktop.css";

export function AdminManagerEmployeeDetailPanel({
  employee,
  currentUser,
  onBack,
  onSaved,
}) {
  const isDesktopDetail = useIsDesktopViewport();
  const [editPhone, setEditPhone] = useState(employee?.phone || "");
  const [editHourlyRate, setEditHourlyRate] = useState(employee?.hourly_rate || "");
  const [editNotes, setEditNotes] = useState(employee?.notes || "");

  useEffect(() => {
    setEditPhone(employee?.phone || "");
    setEditHourlyRate(employee?.hourly_rate || "");
    setEditNotes(employee?.notes || "");
  }, [employee]);

  const handleSaveEmployee = async () => {
    try {
      await saveManagedEmployee(currentUser, {
        supabase_user_id: employee.supabase_user_id,
        phone: editPhone,
        hourly_rate: editHourlyRate,
        notes: editNotes,
      });

      alert("Employee updated successfully");

      onSaved?.({
        ...employee,
        phone: editPhone,
        hourly_rate: editHourlyRate,
        notes: editNotes,
      });
    } catch (err) {
      console.error("SAVE EMPLOYEE ERROR:", err);
      alert("Failed to save employee");
    }
  };

  if (isDesktopDetail) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="EMPLOYEE DETAILS" onBack={onBack} backLabel="Manager Tools" />
        <div className="adm-main-side">
          <AdminFormCard title="EMPLOYEE SETTINGS">
            <div className="adm-fields">
              <CPInput label="Phone" type="tel" value={editPhone} onChange={setEditPhone} />
              <CPInput
                label="User Rate"
                type="number"
                value={editHourlyRate}
                onChange={setEditHourlyRate}
                aria-label="User Rate"
              />
              <p className="adm-note adm-wide" style={{ margin: 0 }}>
                Personal hourly rate (not a payroll matrix value).
              </p>
              <CPTextarea
                className="adm-wide"
                label="Notes"
                value={editNotes}
                onChange={setEditNotes}
                rows={4}
              />
            </div>
            <div className="adm-actions">
              <button type="button" className="kpi-back-btn adm-primary" onClick={handleSaveEmployee}>
                Save Employee
              </button>
            </div>
          </AdminFormCard>
          <AdminFormCard title="EMPLOYEE">
            <dl className="adm-kv">
              <dt>Name</dt>
              <dd>{employee.employee_name}</dd>
              <dt>Email</dt>
              <dd>{employee.email || "Not Available"}</dd>
              <dt>Role</dt>
              <dd>{employee.role}</dd>
            </dl>
          </AdminFormCard>
        </div>
      </div>
    );
  }

  return (
    <div className="intel-screen">
      <AdminLegacyHeader title="EMPLOYEE DETAILS" onBack={onBack} backLabel="Manager Tools" />

      <AdminFormCard>
        <div className="section-heading">NAME</div>
        <p className="profile-value">{employee.employee_name}</p>

        <div className="section-heading">EMAIL</div>
        <p className="profile-value">{employee.email || "Not Available"}</p>

        <div className="section-heading">ROLE</div>
        <p className="profile-value">{employee.role}</p>

        <CPInput
          label="Phone"
          type="tel"
          value={editPhone}
          onChange={setEditPhone}
        />

        <CPInput
          label="User Rate"
          type="number"
          value={editHourlyRate}
          onChange={setEditHourlyRate}
          aria-label="User Rate"
        />
        <p className="profile-value" style={{ marginTop: "6px", opacity: 0.8 }}>
          Personal hourly rate (not a payroll matrix value).
        </p>

        <CPTextarea
          label="Notes"
          value={editNotes}
          onChange={setEditNotes}
          rows={4}
        />

        <button type="button" className="kpi-back-btn" onClick={handleSaveEmployee}>
          Save Employee
        </button>
      </AdminFormCard>
    </div>
  );
}

export function AdminManagerToolsPanel({
  currentUser,
  onBack,
  showLegacyHeader = true,
}) {
  const isDesktop = useIsDesktopViewport();
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const loadManagedEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const data = await listManagedEmployees(
        currentUser,
        currentUser.supabase_user_id,
      );
      setEmployees(data);
    } catch (err) {
      console.error("LOAD EMPLOYEES ERROR:", err);
    } finally {
      setLoadingEmployees(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadManagedEmployees();
  }, [loadManagedEmployees]);

  if (selectedEmployee) {
    return (
      <AdminManagerEmployeeDetailPanel
        employee={selectedEmployee}
        currentUser={currentUser}
        onBack={() => setSelectedEmployee(null)}
        onSaved={(updated) => {
          setSelectedEmployee(updated);
          loadManagedEmployees();
        }}
      />
    );
  }

  const desktopContent = loadingEmployees ? (
    <p className="adm-note">Loading employees...</p>
  ) : employees.length === 0 ? (
    <DeskEmpty title="No employees assigned." />
  ) : (
    <DeskTable
      caption="Assigned employees"
      rows={employees}
      rowKey={(employee) => employee.supabase_user_id}
      onOpen={(employee) => setSelectedEmployee(employee)}
      openLabel={(employee) => `Open ${employee.employee_name}`}
      columns={[
        {
          key: "name",
          label: "Assigned employees",
          width: "40%",
          primary: true,
          render: (employee) => (
            <span className="dt-item__text">
              <span className="dt-strong">{employee.employee_name}</span>
              <span className="dt-sub">{employee.email || "—"}</span>
            </span>
          ),
        },
        {
          key: "role",
          label: "Role",
          render: (employee) => <span className="dt-muted">{employee.role || "—"}</span>,
        },
        {
          key: "phone",
          label: "Phone",
          render: (employee) => <span className="dt-muted">{employee.phone || "—"}</span>,
        },
      ]}
    />
  );

  const content = isDesktop ? desktopContent : (
    <AdminFormCard title="MANAGER TOOLS">
      {loadingEmployees ? (
        <p style={{ color: "white" }}>Loading employees...</p>
      ) : (
        <>
          <div className="section-heading">ASSIGNED EMPLOYEES</div>

          {employees.length === 0 ? (
            <p className="profile-value">No employees assigned.</p>
          ) : (
            employees.map((employee) => (
              <AdminListRow
                key={employee.supabase_user_id}
                onClick={() => {
                  console.log("CLICKED:", employee);
                  setSelectedEmployee(employee);
                }}
              >
                {employee.employee_name}
              </AdminListRow>
            ))
          )}
        </>
      )}
    </AdminFormCard>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="MANAGER TOOLS" onBack={onBack} />
        {content}
      </div>
    );
  }

  return content;
}
